/**
 * `core/embla` — la única puerta a Embla en el tema.
 *
 * Rieles, galerías de card, hero, PDP, reels, recos del carrito y subcats PLP.
 * Los sistemas no importan `embla-carousel` por su cuenta.
 *
 * Embla es una mejora, no el carrusel: la base sigue siendo `overflow-x: auto` +
 * `scroll-snap`. `montar()` pone `is-enhanced` en el host y el CSS apaga el
 * scroll nativo. Si el import falla, el riel se queda scrolleable.
 *
 * CON EL DEDO NO HAY EMBLA. Bajo `(pointer: coarse)` el carrusel es el scroller CSS
 * (gesto, inercia y snap del navegador, como en mammut.com) y la librería solo entra
 * con puntero fino, donde aporta el arrastre con ratón, las flechas y los dots. Cada
 * sistema pregunta `rielNativo()` al montar y vigila el cambio con `vigilarRielNativo()`.
 *
 * Embla NO reparenta las celdas: el flex del tema (gap, anchos por preset) se
 * queda. El puente CSS es `overflow: hidden`/`clip` en el viewport y
 * `touch-action: pan-y pinch-zoom` en la pista.
 */

import { prefersReduced } from "motion/tokens";

/** @type {Promise<any> | null} */
let nucleo = null;
/** @type {Promise<any> | null} */
let clases = null;

/**
 * @returns {Promise<any>} el constructor `EmblaCarousel`
 */
export async function cargarEmbla() {
  if (!nucleo) {
    nucleo = import("embla")
      .then((m) => m.default)
      .catch((error) => {
        nucleo = null;
        throw error;
      });
  }
  try {
    return await nucleo;
  } catch (error) {
    nucleo = null;
    throw error;
  }
}

/**
 * Plugin oficial de class names. Lo pide quien monta (riel: `is-selected`).
 * @returns {Promise<any>}
 */
export async function cargarClassNames() {
  if (!clases) {
    clases = import("embla/class-names")
      .then((m) => m.default)
      .catch((error) => {
        clases = null;
        throw error;
      });
  }
  try {
    return await clases;
  } catch (error) {
    clases = null;
    throw error;
  }
}

let precalentado = false;

/**
 * Empieza YA la descarga de `vendor.embla.js` y el plugin de class-names.
 * Save-Data no adelanta.
 */
export function precalentar() {
  if (precalentado) return;
  const red = /** @type {any} */ (navigator).connection;
  if (red?.saveData) return;
  precalentado = true;
  void Promise.all([cargarEmbla(), cargarClassNames()]).catch(() => {
    precalentado = false;
  });
}

precalentar();

/** Puntero grueso: el carrusel es el scroller CSS y Embla no monta. */
export const TACTIL = "(pointer: coarse)";
const mqlTactil = window.matchMedia(TACTIL);

/** @returns {boolean} el riel debe quedarse nativo (dedo). */
export function rielNativo() {
  return mqlTactil.matches;
}

/**
 * Llama `fn(nativo)` cuando cambia el tipo de puntero (DevTools, híbridos). No llama
 * al registrar: quien monta ya leyó `rielNativo()` en su gate.
 *
 * @param {(nativo: boolean) => void} fn
 * @returns {() => void}
 */
export function vigilarRielNativo(fn) {
  const onChange = () => fn(mqlTactil.matches);
  mqlTactil.addEventListener("change", onChange);
  return () => mqlTactil.removeEventListener("change", onChange);
}

/**
 * BUCLE PARA UN SCROLLER CSS (dedo). Clona el JUEGO ENTERO de slides a cada lado de la
 * pista —[clones][reales][clones]— y, al asentar el scroll, recoloca por el periodo
 * (el ancho del juego): si se asentó sobre un clon, se salta en seco a la copia real,
 * que es idéntica y está rodeada de lo mismo, así que no se ve. Con el juego entero de
 * margen, los swipes se encadenan sin esperar a nada: hay tantos slides de colchón como
 * fotos. Un clon por lado —lo primero que se probó— obligaba a parar el gesto en cada
 * vuelta, porque más allá del clon no había nada hasta recolocar.
 *
 * Solo hay bucle si el juego real desborda el viewport (`desbordaReal`). Los clones los
 * marca quien llama (`marcarClon`): qué atributos quitar para que no cuenten como slide,
 * `aria-hidden`, foco fuera; `vecino` señala el clon pegado al juego real por cada lado,
 * el único que se ve antes de recolocar (sus fotos conviene cargarlas ya).
 *
 * `colchon: "minimo"` clona solo lo necesario —lo que cabe en el viewport más dos— en vez
 * del juego entero: para un riel de doce tarjetas de producto, diez clones y no
 * veinticuatro. La recolocación sigue siendo invisible mientras el colchón cubra el
 * viewport; el encadenado de swipes sin asentar tiene ese tope, que en la práctica no se
 * alcanza (cada swipe asienta en ~300 ms).
 *
 * @param {{
 *   viewport: HTMLElement,
 *   pista: HTMLElement,
 *   slides: () => HTMLElement[],
 *   marcarClon: (clon: HTMLElement, vecino: boolean) => void,
 *   alAsentar?: (indice: number) => void,
 *   colchon?: "todo" | "minimo",
 *   signal?: AbortSignal,
 * }} ctx
 */
export function bucleNativo({ viewport, pista, slides, marcarClon, alAsentar, colchon = "todo", signal }) {
  /** @type {HTMLElement[]} */
  let delante = [];
  /** @type {HTMLElement[]} */
  let detras = [];
  let timer = 0;

  const relleno = () => {
    const css = getComputedStyle(viewport);
    return Number.parseFloat(css.scrollPaddingLeft) || Number.parseFloat(css.paddingLeft) || 0;
  };
  /** `scrollLeft` que deja `el` en su parada de snap (borde de contenido del viewport). */
  const posSnap = (el) =>
    viewport.scrollLeft + el.getBoundingClientRect().left - viewport.getBoundingClientRect().left - relleno();
  const activo = () => detras.length > 0;
  const desbordaReal = () => {
    const r = slides();
    if (r.length < 2) return false;
    return r[r.length - 1].getBoundingClientRect().right - r[0].getBoundingClientRect().left > viewport.clientWidth + 2;
  };
  const periodo = () => (activo() ? detras[0].getBoundingClientRect().left - slides()[0].getBoundingClientRect().left : 0);

  /** Índice de la foto REAL más cercana a la parada actual (los clones cuentan como su original). */
  const indice = () => {
    const r = slides();
    const n = r.length;
    if (!n) return 0;
    const todos = activo() ? [...delante, ...r, ...detras] : r;
    const sl = viewport.scrollLeft;
    let mejor = 0;
    let d = Infinity;
    todos.forEach((el, k) => {
      const dist = Math.abs(posSnap(el) - sl);
      if (dist < d) {
        d = dist;
        mejor = k;
      }
    });
    // Los clones delanteros son los ÚLTIMOS `k` reales: restar `k` y dar la vuelta.
    return activo() ? (((mejor - delante.length) % n) + n) % n : mejor;
  };

  /** Pone los clones si hay bucle que hacer. Devuelve si está activo. */
  const poner = () => {
    if (activo()) return true;
    const r = slides();
    if (r.length < 2 || !desbordaReal()) return false;
    const clonar = (slide, vecino) => {
      const clon = /** @type {HTMLElement} */ (slide.cloneNode(true));
      marcarClon(clon, vecino);
      return clon;
    };
    let k = r.length;
    if (colchon === "minimo") {
      const paso = posSnap(r[1]) - posSnap(r[0]);
      const visibles = paso > 0 ? Math.ceil(viewport.clientWidth / paso) : r.length;
      k = Math.min(r.length, visibles + 2);
    }
    delante = r.slice(r.length - k).map((slide, i) => clonar(slide, i === k - 1));
    detras = r.slice(0, k).map((slide, i) => clonar(slide, i === 0));
    const primero = r[0];
    delante.forEach((clon) => pista.insertBefore(clon, primero));
    detras.forEach((clon) => pista.appendChild(clon));
    return true;
  };

  /** Quita los clones y deja el scroll sobre la misma foto real. */
  const quitar = () => {
    if (!activo()) return;
    const i = indice();
    [...delante, ...detras].forEach((clon) => clon.remove());
    delante = [];
    detras = [];
    const r = slides();
    if (r[i] && viewport.clientWidth > 1) viewport.scrollTo({ left: posSnap(r[i]), behavior: "instant" });
  };

  /** Asentado sobre los clones: la misma vista, un periodo más adentro. */
  const recolocar = () => {
    if (!activo()) return false;
    const p = periodo();
    if (p < 1) return false;
    const inicioReal = posSnap(slides()[0]);
    const sl = viewport.scrollLeft;
    if (sl < inicioReal - 1) {
      viewport.scrollTo({ left: sl + p, behavior: "instant" });
      return true;
    }
    if (sl >= inicioReal + p - 1) {
      viewport.scrollTo({ left: sl - p, behavior: "instant" });
      return true;
    }
    return false;
  };

  /**
   * Ir a la foto real `i`. Por defecto por la copia MÁS CERCANA —del último al primero
   * es un paso corto por el clon vecino; al asentar se recoloca—; `soloReal` fuerza la
   * copia real (colocación inicial, cambios de tamaño). `behavior: "auto"` cede al
   * `scroll-behavior` del CSS, que ya respeta `prefers-reduced-motion`.
   * @param {number} i @param {ScrollBehavior} [behavior] @param {boolean} [soloReal]
   */
  const colocar = (i, behavior = "auto", soloReal = false) => {
    const r = slides();
    const n = r.length;
    if (!n || viewport.clientWidth < 2) return;
    const k = ((i % n) + n) % n;
    // Copias de la foto `k`: delante están los últimos `delante.length` reales; detrás, los primeros.
    const enDelante = k - (n - delante.length);
    const candidatos =
      activo() && !soloReal
        ? [enDelante >= 0 ? delante[enDelante] : null, r[k], k < detras.length ? detras[k] : null].filter(Boolean)
        : [r[k]];
    const sl = viewport.scrollLeft;
    let destino = candidatos[0];
    let d = Infinity;
    for (const c of candidatos) {
      const dist = Math.abs(posSnap(c) - sl);
      if (dist < d) {
        d = dist;
        destino = c;
      }
    }
    const left = posSnap(destino);
    if (Math.abs(left - sl) <= 1) return;
    viewport.scrollTo({ left, behavior });
  };

  // El asentado: `scrollend` donde existe y un respaldo de 120 ms tras el último `scroll`.
  const asentar = () => {
    window.clearTimeout(timer);
    timer = 0;
    recolocar();
    alAsentar?.(indice());
  };
  viewport.addEventListener(
    "scroll",
    () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(asentar, 120);
    },
    { passive: true, signal },
  );
  viewport.addEventListener("scrollend", asentar, { signal });

  return { poner, quitar, activo, indice, recolocar, colocar, desbordaReal, posSnap };
}

const BOOLEANOS = new Set([
  "loop",
  "dragFree",
  "skipSnaps",
  "watchDrag",
  "watchResize",
  "watchSlides",
  "watchFocus",
  "active",
]);

const NUMEROS = new Set([
  "duration",
  "dragThreshold",
  "startIndex",
  "inViewThreshold",
  "slidesToScroll",
]);

/**
 * @param {string} nombre
 * @param {string} valor
 */
function parsear(nombre, valor) {
  if (BOOLEANOS.has(nombre) && (valor === "true" || valor === "false")) return valor === "true";
  if (NUMEROS.has(nombre) && valor !== "" && valor !== "auto" && !Number.isNaN(Number(valor))) {
    return Number(valor);
  }
  return valor;
}

/**
 * Lee `data-embla-*` del elemento. `data-embla-options` admite un JSON extra.
 *
 * @param {HTMLElement} el
 * @returns {Record<string, unknown>}
 */
export function opcionesDesde(el) {
  /** @type {Record<string, unknown>} */
  const out = {};

  for (const [clave, valor] of Object.entries(el.dataset)) {
    if (!clave.startsWith("embla")) continue;
    if (clave === "embla" || clave === "emblaOptions") continue;
    const nombre = clave.slice(5, 6).toLowerCase() + clave.slice(6);
    if (nombre) out[nombre] = parsear(nombre, valor);
  }

  const json = el.dataset.emblaOptions;
  if (json) {
    try {
      Object.assign(out, JSON.parse(json));
    } catch {
      /* JSON roto: se ignora */
    }
  }

  return out;
}

const MARCA_ARRASTRE = "data-carousel-dragging";

/** Cuerpos de scroll ya parcheados. `reInit` crea uno nuevo. */
const cuerposSinResorte = new WeakSet();

/**
 * Embla amortigua hacia el snap (resorte) y, sin `loop`, hace rubber-band en el
 * borde. El vendor no se toca: se parchea `internalEngine`.
 *
 * Arrastre 1:1 (sin lag). Al soltar, un lerp que nunca se pasa del snap —el
 * seek original es un muelle y se descarta la posición. Tope duro si no hay bucle.
 *
 * @param {any} api
 */
function sinResorte(api) {
  const aplicar = () => {
    const engine = typeof api.internalEngine === "function" ? api.internalEngine() : null;
    if (!engine?.scrollBody || !engine.scrollBounds || !engine.dragHandler) return;

    engine.scrollBounds.toggleActive(false);

    const body = engine.scrollBody;
    if (cuerposSinResorte.has(body)) return;
    cuerposSinResorte.add(body);

    const drag = engine.dragHandler;
    const useFriction = body.useFriction.bind(body);
    const useDuration = body.useDuration.bind(body);
    const seek = body.seek.bind(body);
    const enBucle = () => Boolean(engine.options?.loop);

    body.useFriction = (n) => useFriction(drag.pointerDown() ? 0 : n);
    body.useDuration = (n) => useDuration(drag.pointerDown() ? 0 : n);

    if (engine.limit && engine.target) {
      const origAdd = engine.target.add.bind(engine.target);
      engine.target.add = (n) => {
        origAdd(n);
        if (drag.pointerDown() && !enBucle()) {
          engine.target.set(engine.limit.constrain(engine.target.get()));
        }
      };
    }

    body.seek = () => {
      if (drag.pointerDown()) {
        seek();
        if (!enBucle() && engine.limit) {
          const tope = engine.limit.constrain(engine.target.get());
          engine.target.set(tope);
          engine.location.set(tope);
          engine.previousLocation.set(tope);
        }
        return body;
      }

      const prev = engine.location.get();
      seek();
      if (!body.duration()) return body;

      const tgt = engine.target.get();
      const delta = tgt - prev;
      const abs = Math.abs(delta);
      if (abs < 0.5) {
        engine.location.set(tgt);
        engine.previousLocation.set(tgt);
        useDuration(0);
        return body;
      }
      engine.previousLocation.set(prev);
      engine.location.set(prev + delta * 0.21);
      return body;
    };
  };

  aplicar();
  api.on("reInit", aplicar);
}

/**
 * Si Embla apagó `loop` (no hay pista de sobra), vuelve `containScroll` para
 * no inventar paradas vacías ni flechas sobre un riel que cabe entero.
 *
 * @param {any} api
 */
function asentarBucle(api) {
  const engine = typeof api.internalEngine === "function" ? api.internalEngine() : null;
  if (!engine?.options?.loop && engine?.options?.containScroll !== "trimSnaps") {
    api.reInit({ loop: false, containScroll: "trimSnaps" });
  }
}

/** @param {any} api */
export function enBucle(api) {
  const engine = typeof api.internalEngine === "function" ? api.internalEngine() : null;
  return Boolean(engine?.options?.loop);
}

/**
 * Índice de snap que contiene el slide `i`. Con `slidesToScroll: "auto"` no coinciden.
 *
 * @param {any} api
 * @param {number} slideIndex
 * @returns {number}
 */
export function snapDeSlide(api, slideIndex) {
  const engine = typeof api.internalEngine === "function" ? api.internalEngine() : null;
  const registry = engine?.slideRegistry;
  if (Array.isArray(registry)) {
    const i = registry.findIndex((grupo) => Array.isArray(grupo) && grupo.includes(slideIndex));
    if (i >= 0) return i;
  }
  return slideIndex;
}

/**
 * El CSS marca la pista como carril horizontal (`content` del `::after`).
 *
 * @param {HTMLElement} el
 * @returns {boolean}
 */
export function pistaEsCarril(el) {
  const c = getComputedStyle(el, "::after").content || "";
  return /carousel|embla/i.test(c.replace(/['"]/g, ""));
}

/**
 * Llama `fn(activo)` al cambiar el marcador de carril (breakpoint o clase del host).
 *
 * @param {HTMLElement} pista
 * @param {(activo: boolean) => void} fn
 * @param {{ anfitrion?: HTMLElement | null }} [opts]
 * @returns {() => void}
 */
export function vigilarCarril(pista, fn, { anfitrion = null } = {}) {
  let previo = /** @type {boolean | null} */ (null);
  const mirar = () => {
    const ahora = pistaEsCarril(pista);
    if (ahora === previo) return;
    previo = ahora;
    fn(ahora);
  };
  mirar();
  // El marcador cambia por ancho (rejilla editorial ≥990px) y por puntero (con el
  // dedo ningún modo es carril Embla): se vigilan los dos.
  const mqls = [window.matchMedia("(min-width: 990px)"), mqlTactil];
  mqls.forEach((mql) => mql.addEventListener("change", mirar));
  const raiz = anfitrion || pista;
  const mo = new MutationObserver(mirar);
  mo.observe(raiz, { attributes: true, attributeFilter: ["class"] });
  return () => {
    mqls.forEach((mql) => mql.removeEventListener("change", mirar));
    mo.disconnect();
  };
}

/** @param {any} api */
function aislarNoSeleccionados(api) {
  const sync = () => {
    const i = api.selectedScrollSnap();
    api.slideNodes().forEach((el, n) => {
      if (n === i) el.removeAttribute("inert");
      else el.setAttribute("inert", "");
    });
  };
  api.on("select", sync);
  api.on("reInit", sync);
  sync();
  return () => {
    api.slideNodes().forEach((el) => el.removeAttribute("inert"));
  };
}

/**
 * Alto del viewport = alto del slide activo (hero). Embla no trae `adaptiveHeight`.
 *
 * @param {any} api
 * @param {HTMLElement} viewport
 * @returns {() => void}
 */
function altoDelActivo(api, viewport) {
  const sync = () => {
    const slide = api.slideNodes()[api.selectedScrollSnap()];
    if (!(slide instanceof HTMLElement)) return;
    viewport.style.height = `${Math.round(slide.getBoundingClientRect().height)}px`;
  };
  api.on("select", sync);
  api.on("reInit", sync);
  api.on("settle", sync);
  /** @type {ResizeObserver | null} */
  let ro = null;
  if ("ResizeObserver" in window) {
    ro = new ResizeObserver(sync);
    const observar = () => {
      ro?.disconnect();
      api.slideNodes().forEach((el) => {
        if (el instanceof Element) ro?.observe(el);
      });
    };
    observar();
    api.on("reInit", observar);
  }
  sync();
  return () => {
    ro?.disconnect();
    viewport.style.height = "";
  };
}

/**
 * La marca es para un ARRASTRE, no para un `pointerDown`. Wishlist y el lightbox
 * de la PDP miran `data-carousel-dragging` en el `click` (captura): si se pintara
 * al posar el dedo, un clic limpio sobre un corazón DENTRO del riel —el corazón
 * vive en el slide, fuera de la galería de la card, así que el riel SÍ lo oye—
 * se ignoraría. Embla emite `pointerDown` en el mousedown; el `click` llega
 * antes de que el `setTimeout` del `pointerUp` borre la marca.
 *
 * `scroll` con el puntero abajo es el equivalente a «empezó a arrastrar»:
 * hubo movimiento, no un clic.
 *
 * @param {any} api
 */
function marcarArrastres(api) {
  let down = false;
  api.on("pointerDown", () => {
    down = true;
  });
  api.on("scroll", () => {
    if (down) document.documentElement.setAttribute(MARCA_ARRASTRE, "");
  });
  api.on("pointerUp", () => {
    down = false;
    setTimeout(() => document.documentElement.removeAttribute(MARCA_ARRASTRE));
  });
}

/** @returns {boolean} */
export function arrastrandoCarrusel() {
  return document.documentElement.hasAttribute(MARCA_ARRASTRE);
}

/** @returns {boolean} */
export function instante() {
  return prefersReduced();
}

/**
 * @param {any} api
 * @param {HTMLElement} viewport
 * @param {boolean} wrapSiguiente
 * @param {AbortSignal | undefined} signal
 */
function atarTeclado(api, viewport, wrapSiguiente, signal) {
  /** @param {KeyboardEvent} event */
  const onKey = (event) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      if (api.canScrollNext()) api.scrollNext(instante());
      else if (wrapSiguiente) api.scrollTo(0, true);
    } else if (event.key === "ArrowLeft") {
      if (api.canScrollPrev()) {
        event.preventDefault();
        api.scrollPrev(instante());
      } else if (wrapSiguiente) {
        event.preventDefault();
        api.scrollTo(api.scrollSnapList().length - 1, true);
      }
    }
  };
  viewport.addEventListener("keydown", onKey, signal ? { signal } : undefined);
}

/**
 * Rueda horizontal → un snap. La vertical se deja a la página.
 *
 * @param {any} api
 * @param {HTMLElement} viewport
 * @param {boolean} wrapSiguiente
 * @param {AbortSignal | undefined} signal
 */
function atarRueda(api, viewport, wrapSiguiente, signal) {
  let acc = 0;
  /** @param {WheelEvent} event */
  const onWheel = (event) => {
    if (event.ctrlKey) return;
    const ax = Math.abs(event.deltaX);
    const ay = Math.abs(event.deltaY);
    if (ax <= ay || ax < 2) return;
    event.preventDefault();
    acc += event.deltaX;
    if (Math.abs(acc) < 48) return;
    const dir = acc > 0 ? 1 : -1;
    acc = 0;
    if (dir > 0) {
      if (api.canScrollNext()) api.scrollNext();
      else if (wrapSiguiente) api.scrollTo(0, true);
    } else if (api.canScrollPrev()) {
      api.scrollPrev();
    } else if (wrapSiguiente) {
      api.scrollTo(api.scrollSnapList().length - 1, true);
    }
  };
  viewport.addEventListener("wheel", onWheel, signal ? { passive: false, signal } : { passive: false });
}

/**
 * @param {any} EmblaCarousel
 * @param {HTMLElement} viewport
 * @param {object} ctx
 * @param {HTMLElement} ctx.host
 * @param {HTMLElement | null} [ctx.container]
 * @param {string | HTMLElement[] | null} [ctx.slides]
 * @param {Record<string, unknown>} [ctx.opciones]
 * @param {any[]} [ctx.plugins]
 * @param {AbortSignal} [ctx.signal]
 * @param {boolean} [ctx.rueda]
 * @param {boolean} [ctx.wrapSiguiente] «siguiente» en el último vuelve a 0
 * @param {boolean} [ctx.aislar] `inert` en slides no seleccionados (hero)
 * @param {boolean} [ctx.altoAdaptativo] alto del viewport = slide activo (hero)
 * @param {HTMLElement | null} [ctx.teclado] nodo que recibe flechas; por defecto el viewport
 * @returns {any}
 */
export function montar(
  EmblaCarousel,
  viewport,
  {
    host,
    container = null,
    slides = null,
    opciones = {},
    plugins = [],
    signal,
    rueda = false,
    wrapSiguiente = false,
    aislar = false,
    altoAdaptativo = false,
    teclado = null,
  },
) {
  host.classList.add("is-enhanced");

  /** @type {Record<string, unknown>} */
  const opts = {
    align: "start",
    containScroll: "trimSnaps",
    skipSnaps: false,
    watchFocus: true,
    duration: 29,
    ...opciones,
  };
  if (container) opts.container = container;
  if (slides) opts.slides = slides;

  const api = EmblaCarousel(viewport, opts, plugins);

  sinResorte(api);
  asentarBucle(api);
  marcarArrastres(api);
  atarTeclado(api, teclado || viewport, wrapSiguiente, signal);
  if (rueda) atarRueda(api, viewport, wrapSiguiente, signal);

  /** @type {Array<() => void>} */
  const limpiezas = [];
  if (aislar) limpiezas.push(aislarNoSeleccionados(api));
  if (altoAdaptativo) limpiezas.push(altoDelActivo(api, viewport));

  const destruir = api.destroy.bind(api);
  api.destroy = () => {
    limpiezas.forEach((fn) => fn());
    destruir();
    host.classList.remove("is-enhanced", "is-settled");
  };

  return api;
}

/**
 * @param {HTMLElement} el
 * @param {() => void} fn
 * @param {string} [rootMargin]
 * @returns {() => void}
 */
export function alVer(el, fn, rootMargin = "200px") {
  if (!("IntersectionObserver" in window)) {
    fn();
    return () => {};
  }

  const observer = new IntersectionObserver(
    (entradas) => {
      if (!entradas.some((entrada) => entrada.isIntersecting)) return;
      observer.disconnect();
      fn();
    },
    { rootMargin },
  );
  observer.observe(el);
  return () => observer.disconnect();
}
